import { Building2Icon, MapPinIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { KeywordHighlightedText } from "@/components/KeywordHighlightedText";

import {
  companyMatchTone,
  useCustomerCompanyMatches,
} from "@/hooks/useCustomerCompanyMatches";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import {
  ClassifiedKeyword,
  CompensationType,
  WorkExperienceType,
} from "@/types";

export const WorkExperience = ({
  highlightKeywords,
  workExperience,
}: {
  highlightKeywords?: ClassifiedKeyword[];
  workExperience: WorkExperienceType;
}) => {
  /* React Hooks */
  const { matchCompany } = useCustomerCompanyMatches();

  const dateDisplay =
    dates.prepareDateDisplayForCertificationOrEducationOrWorkExperience({
      startMonth: workExperience.startMonth ?? undefined,
      startYear: workExperience.startYear ?? undefined,
      endMonth: workExperience.endMonth ?? undefined,
      endYear: workExperience.endYear ?? undefined,
      current: workExperience.currentPosition ?? undefined,
    });

  const tenure =
    dates.calculateTenureForCertificationOrEducationOrWorkExperience({
      startMonth: workExperience.startMonth,
      startYear: workExperience.startYear,
      endMonth: workExperience.endMonth,
      endYear: workExperience.endYear,
      current: workExperience.currentPosition,
    });

  const payUnit =
    workExperience.compensationType === CompensationType.HOURLY ? "hr" : "yr";

  // Drizzle returns numeric DB columns as strings for precision, so we parse to Number for comma formatting
  const finalPayDisplay = workExperience.finalPay
    ? `$${Number(workExperience.finalPay).toLocaleString()}/${payUnit}`
    : null;
  const startPayDisplay = workExperience.startPay
    ? `$${Number(workExperience.startPay).toLocaleString()}/${payUnit}`
    : null;

  const cleanedDescriptions =
    workExperience.descriptions?.filter((d) => d.trim()) ?? [];

  // Resume parsing can produce work experiences with missing titles or companies.
  // Show placeholders so staffers can spot and fix incomplete entries.
  const title = workExperience.title?.trim();
  const companyName =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    workExperience.internalCompany?.name.trim() ||
    workExperience.company?.trim();
  const dot = <span className="text-slate-500">·</span>;

  const renderCompanyName = () => {
    if (!companyName) {
      return (
        <Text className="text-sm font-normal italic text-red-400">
          [Missing Company]
        </Text>
      );
    }
    // Customer match: color the employer name so the risk reads inline.
    const match = matchCompany(companyName, workExperience.currentPosition);
    if (match) {
      const tone = companyMatchTone(match.reason);
      return (
        <Text
          className={cn(
            "rounded-md border px-1.5 py-0.5 text-sm font-medium",
            tone.container,
            tone.content,
          )}
        >
          {companyName}
        </Text>
      );
    }
    return (
      <Text className="text-sm font-normal text-slate-600">{companyName}</Text>
    );
  };
  const hasLocation = !!workExperience.location;
  const hasPay = !!finalPayDisplay;

  return (
    <Row className="items-start gap-4">
      <Row className="h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
        {workExperience.internalCompany?.logoUrl ? (
          <img
            alt={workExperience.internalCompany.name}
            className="h-full w-full object-cover"
            src={workExperience.internalCompany.logoUrl}
          />
        ) : (
          <Building2Icon className="h-5 w-5 text-slate-400" />
        )}
      </Row>
      <Column className="min-w-0 flex-1 gap-0.5">
        <Row className="min-h-11 items-stretch justify-between gap-3 pb-1">
          <Column className="min-w-0 justify-between gap-0.5">
            <Row>
              {title ? (
                <Text className="text-sm font-medium capitalize text-slate-900">
                  {title}
                </Text>
              ) : (
                <Text className="text-sm font-semibold uppercase italic tracking-wide text-red-400">
                  [Missing Title]
                </Text>
              )}
            </Row>
            <Row>{renderCompanyName()}</Row>
          </Column>
          <Column className="shrink-0 items-end justify-between gap-1.5 text-right">
            {dateDisplay ? (
              <Text className="text-sm font-normal text-slate-500">
                {dateDisplay}
              </Text>
            ) : null}
            {tenure ? (
              <Text className="text-sm font-normal text-slate-500">
                {tenure}
              </Text>
            ) : null}
          </Column>
        </Row>
        {(hasLocation || hasPay) && (
          <Row className="flex-wrap items-center gap-x-2 text-sm text-slate-500">
            {hasLocation && (
              <Row className="items-center gap-1 text-xs">
                <MapPinIcon className="h-3.5 w-3.5" />
                <span>{workExperience.location}</span>
              </Row>
            )}
            {hasLocation && hasPay && dot}
            {hasPay && (
              <Row className="items-baseline gap-1.5">
                {startPayDisplay ? (
                  <span className="text-xs line-through">
                    {startPayDisplay}
                  </span>
                ) : null}
                <span>{finalPayDisplay}</span>
              </Row>
            )}
          </Row>
        )}
        {cleanedDescriptions.length > 0 ? (
          <ul className="mt-2 list-disc pl-5">
            {cleanedDescriptions.map((description, index) => (
              <li
                className="mt-1 text-sm font-light leading-relaxed text-slate-600"
                key={index}
              >
                <KeywordHighlightedText
                  highlightKeywords={highlightKeywords}
                  text={description}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </Column>
    </Row>
  );
};
